import type { CsvRow, TeamDraft } from "../types/draft";
import { parsePickedAt } from "./playerHelpers";

// ── Local chart-specific types ────────────────────────────────────

interface UniquePlayerEntry {
  name: string;
  pickNumbers: number[];
  count: number;
  avgAdp: number | null;
}

interface UniqueTeamEntry {
  team: string;
  uniquePlayers: number;
  players: UniquePlayerEntry[];
}

interface StructureEntry {
  structure: string;
  count: number;
  percent: string;
}

interface SlotEntry {
  slot: string;
  count: number;
}

type ChartRow = Record<string, string | number>;

// ── Helpers ───────────────────────────────────────────────────────

export function getUniquePlayersByTeam(data: CsvRow[]): UniqueTeamEntry[] {
  // Map: team -> { playerName -> [pickNumbers] }
  const teamMap: Record<string, Record<string, number[]>> = {};
  data.forEach(row => {
    const team = (row["Team"] || "").trim();
    const first = (row["First Name"] || "").trim();
    const last = (row["Last Name"] || "").trim();
    const playerName = `${first} ${last}`.trim();
    const pickNumber = Number(row["Pick Number"]);
    if (!team || !playerName) return;
    if (!teamMap[team]) teamMap[team] = {};
    if (!teamMap[team][playerName]) teamMap[team][playerName] = [];
    if (!isNaN(pickNumber)) teamMap[team][playerName].push(pickNumber);
  });
  return Object.entries(teamMap)
    .map(([team, playersObj]) => {
      const players: UniquePlayerEntry[] = Object.entries(playersObj).map(([name, pickNumbers]) => {
        const count = pickNumbers.length;
        const avgAdp: number | null = count
          ? pickNumbers.reduce((a, b) => a + b, 0) / count
          : null;
        return { name, pickNumbers, count, avgAdp };
      });
      players.sort((a, b) => {
        if (a.avgAdp === null) return 1;
        if (b.avgAdp === null) return -1;
        if (a.avgAdp !== b.avgAdp) return a.avgAdp - b.avgAdp;
        return a.name.localeCompare(b.name);
      });
      return {
        team,
        uniquePlayers: players.length,
        players,
      };
    })
    .sort((a, b) => b.uniquePlayers - a.uniquePlayers);
}

export function getStackedTeamPositionChartData(
  data: CsvRow[]
): { chartData: ChartRow[]; positions: string[] } {
  const teamPosMap: Record<string, Record<string, number>> = {};
  const positionsSet = new Set<string>();
  data.forEach(row => {
    const team = (row["Team"] || "").trim();
    const pos = (row["Position"] || "").trim();
    if (!team || !pos) return;
    positionsSet.add(pos);
    if (!teamPosMap[team]) teamPosMap[team] = {};
    if (!teamPosMap[team][pos]) teamPosMap[team][pos] = 0;
    teamPosMap[team][pos]++;
  });
  const positions = Array.from(positionsSet);
  const chartData: ChartRow[] = Object.entries(teamPosMap).map(([team, posCounts]) => {
    const row: ChartRow = { team };
    positions.forEach(pos => {
      row[pos] = posCounts[pos] || 0;
    });
    return row;
  });
  return { chartData, positions };
}

export function getDraftSlotStackedByTournament(
  data: CsvRow[]
): { chartData: ChartRow[]; tournaments: string[] } {
  // Returns: { chartData, tournaments }
  const slotMap: Record<string, Record<number, number>> = {}; // tournament -> slot -> count
  const tournaments = new Set<string>();
  data.forEach(row => {
    const tournament = row["Tournament Title"];
    const slot = Number(row["Pick Number"]);
    if (!tournament || isNaN(slot)) return;
    tournaments.add(tournament);
    if (!slotMap[tournament]) slotMap[tournament] = {};
    slotMap[tournament][slot] = (slotMap[tournament][slot] || 0) + 1;
  });
  const allSlots = Array.from({ length: 12 }, (_, i) => i + 1);
  const chartData: ChartRow[] = allSlots.map(slot => {
    const row: ChartRow = { slot: slot.toString() };
    Array.from(tournaments).forEach(tournament => {
      row[tournament] = slotMap[tournament]?.[slot] || 0;
    });
    return row;
  });
  return { chartData, tournaments: Array.from(tournaments) };
}

export function getLineChartDataForPositionByRound(
  data: CsvRow[]
): { data: ChartRow[]; positions: string[] } {
  // Returns: { data: [...], positions: [...] }
  const roundMap: Record<number, Record<string, number>> = {}; // roundNum -> pos -> count
  const positions = new Set<string>();
  let maxRound = 0;
  data.forEach(row => {
    const pos = (row["Position"] || "").trim();
    const pickNumber = Number(row["Pick Number"]);
    if (!pos || isNaN(pickNumber)) return;
    positions.add(pos);
    const round = Math.floor((pickNumber - 1) / 12) + 1;
    maxRound = Math.max(maxRound, round);
    if (!roundMap[round]) roundMap[round] = {};
    roundMap[round][pos] = (roundMap[round][pos] || 0) + 1;
  });
  // For each round, calc % for each position
  const allRounds: ChartRow[] = [];
  for (let round = 1; round <= maxRound; ++round) {
    const row: ChartRow = { round };
    const posCounts = roundMap[round] || {};
    const total = Object.values(posCounts).reduce((a: number, b: number) => a + b, 0) || 1;
    positions.forEach(pos => {
      row[pos] = parseFloat(((posCounts[pos] || 0) / total * 100).toFixed(1));
    });
    allRounds.push(row);
  }
  return { data: allRounds, positions: Array.from(positions) };
}

// The CRITICAL function for My Teams and roster display!
export function getTeamsTableFromCsv(csvRows: CsvRow[]): TeamDraft[] {
  // Group rows by draftId (Draft)
  const teams: Record<string, CsvRow[]> = {};
  csvRows.forEach(row => {
    const draftId = row["Draft"];
    if (!teams[draftId]) teams[draftId] = [];
    teams[draftId].push(row);
  });
  // Build team objects
  return Object.entries(teams).map(([draftId, teamRows]) => {
    // Find the lowest pick number (for draft slot)
    const pickNumbers = teamRows.map(row => Number(row["Pick Number"])).filter(n => !isNaN(n));
    const draftSlot: number | string = pickNumbers.length ? Math.min(...pickNumbers) : "";
    // Prepare parsed date for formatting
    const rawDate = teamRows[0]["Picked At"] || "";
    let parsedDate = "";
    if (rawDate) {
      const dateObj = parsePickedAt(rawDate);
      if (dateObj) {
        parsedDate = dateObj.toISOString();
      }
    }
    return {
      draftId,
      date: parsedDate,
      draftSlot,
      tournament: teamRows[0]["Tournament Title"] || "",
      // A draft belongs to exactly one platform; carry it so CLV can pick the
      // right market (UD vs DK have different ADP boards and draft sizes).
      provider: (teamRows[0]["Provider"] || "").trim() || "Unknown",
      allPlayerNames: teamRows.map(row => `${row["First Name"]} ${row["Last Name"]}`.trim()),
      players: teamRows.map(row => ({
        name: `${row["First Name"]} ${row["Last Name"]}`.trim(),
        firstName: row["First Name"] ? row["First Name"].trim() : "",
        lastName: row["Last Name"] ? row["Last Name"].trim() : "",
        team: row["Team"] ? row["Team"].trim() : "",
        position: row["Position"] ? row["Position"].trim().toUpperCase() : "",
        pickNumber: row["Pick Number"] || "",
      })),
    };
  });
}

export function getStackedPositionCounts(data: CsvRow[]): Record<string, number> {
  // Not currently used, but may be for future stacked charts.
  const counts: Record<string, number> = {};
  data.forEach(row => {
    const pos = (row["Position"] || "").trim();
    if (!pos) return;
    counts[pos] = (counts[pos] || 0) + 1;
  });
  return counts;
}

export function getDraftSlotHistogram(data: CsvRow[]): SlotEntry[] {
  const slotMap: Record<string, number> = {};
  data.forEach(row => {
    const draftId = row["Draft"];
    const slot = parseInt(row["Pick Number"], 10);
    if (!draftId || isNaN(slot)) return;
    if (!slotMap[draftId] || slot < slotMap[draftId]) slotMap[draftId] = slot;
  });
  return Array.from({ length: 12 }, (_, i) => ({
    slot: (i + 1).toString(),
    count: Object.values(slotMap).filter(s => s === i + 1).length,
  }));
}

export function getTeamStructureHistogram(allTeams: TeamDraft[]): StructureEntry[] {
  const structureCounts: Record<string, number> = {};
  allTeams.forEach(team => {
    let qb = 0, rb = 0, wr = 0, te = 0;
    if (Array.isArray(team.players)) {
      team.players.forEach(player => {
        if (player.position === "QB") qb += 1;
        if (player.position === "RB") rb += 1;
        if (player.position === "WR") wr += 1;
        if (player.position === "TE") te += 1;
      });
    }
    const structure = `${qb}-${rb}-${wr}-${te}`;
    structureCounts[structure] = (structureCounts[structure] || 0) + 1;
  });
  const totalTeams = allTeams.length;
  const sorted = Object.entries(structureCounts)
    .map(([structure, count]) => ({
      structure,
      count,
      percent: totalTeams > 0 ? ((count / totalTeams) * 100).toFixed(1) : "0.0",
    }))
    .sort((a, b) => b.count - a.count);
  return sorted;
}

// ── Team Pairing Frequencies ──────────────────────────────────────

export interface TeamPairing {
  team1: string;
  team2: string;
  count: number;
  rate: number;
}

export function getTeamPairingFrequencies(allTeams: TeamDraft[]): {
  pairings: TeamPairing[];
  myTeams: string[];
  totalRosters: number;
} {
  if (allTeams.length === 0) return { pairings: [], myTeams: [], totalRosters: 0 };

  const pairingCounts = new Map<string, number>();
  const allSeenTeams = new Set<string>();

  for (const team of allTeams) {
    const teamsOnRoster = new Set<string>();
    for (const player of team.players) {
      if (player.team) {
        teamsOnRoster.add(player.team);
        allSeenTeams.add(player.team);
      }
    }
    const teamArr = Array.from(teamsOnRoster).sort();
    for (let i = 0; i < teamArr.length; i++) {
      for (let j = i + 1; j < teamArr.length; j++) {
        const key = `${teamArr[i]}|${teamArr[j]}`;
        pairingCounts.set(key, (pairingCounts.get(key) ?? 0) + 1);
      }
    }
  }

  const total = allTeams.length;
  const pairings: TeamPairing[] = Array.from(pairingCounts.entries())
    .map(([key, count]) => {
      const [team1, team2] = key.split("|");
      return { team1, team2, count, rate: count / total };
    })
    .sort((a, b) => b.count - a.count);

  const myTeams = Array.from(allSeenTeams).sort();

  return { pairings, myTeams, totalRosters: total };
}